import classNames from 'classnames';
import { useRef, useState } from 'react';
import { useCloseOnOutsideOrEscape } from '../../../hooks';
import { CardValue, ICard, valueToWord } from '../../../Models/CardModels';
import {
  IVisibleOpponent,
  IVisiblePlay,
  IVisiblePlayer,
  PlayResult,
} from '../../../Models/StateModels';

interface IPlayHistoryProps {
  playHistory: IVisiblePlay[];
  opponents: IVisibleOpponent[];
  player: IVisiblePlayer;
}

function describeCards(cards: ICard[]) {
  const { value } = cards[0];
  if (cards.length > 1) {
    return `${cards.length} ${valueToWord(value, true)}`;
  }
  const article =
    value === CardValue.ACE || value === CardValue.EIGHT ? 'an' : 'a';
  return `${article} ${valueToWord(value)}`;
}

function describeAction(play: IVisiblePlay) {
  if (play.playResult === PlayResult.FORFEIT) {
    return 'forfeited';
  }
  const actions = [];
  if (play.playedCards.length) {
    actions.push(`played ${describeCards(play.playedCards)}`);
  }
  if (play.playResult === PlayResult.PICKED_UP) {
    actions.push('picked up');
  } else if (play.playResult === PlayResult.BURNT) {
    actions.push('burnt the pile');
  }
  return actions.join(' and ');
}

export function describePlay(
  play: IVisiblePlay,
  player: IVisiblePlayer,
  opponents: IVisibleOpponent[]
) {
  const name =
    play.playerIndex === player.playerIndex
      ? 'You'
      : (opponents.find((o) => o.playerIndex === play.playerIndex)?.name ??
        'Someone');
  return `${name} ${describeAction(play)}`;
}

export default function PlayHistory({
  playHistory,
  opponents,
  player,
}: IPlayHistoryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useCloseOnOutsideOrEscape(ref, isOpen, setIsOpen);

  const lastPlay = playHistory[playHistory.length - 1];

  return (
    <div
      ref={ref}
      className="relative flex h-[32px] max-w-full items-center gap-[8px]"
    >
      <span className="min-w-0 truncate text-[14px] font-medium">
        {lastPlay ? describePlay(lastPlay, player, opponents) : 'No plays yet'}
      </span>
      {lastPlay && (
        <button
          aria-label="Play history"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          className={classNames(
            'flex size-[32px] shrink-0 items-center justify-center rounded-full bg-black/24 text-felt-ink',
            'outline-none focus-visible:ring-3 focus-visible:ring-accent-fill'
          )}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </button>
      )}
      {isOpen && (
        <ol
          aria-label="Play history, newest first"
          className="absolute top-full left-1/2 z-10 mt-[8px] -translate-x-1/2 max-h-[240px] w-[260px] divide-y divide-hairline overflow-y-auto rounded-[16px] bg-surface px-[6px] text-ink shadow-dock"
        >
          {playHistory
            .map((play, i) => (
              <li
                key={i}
                className="px-[10px] py-[8px] text-[14px] font-medium"
              >
                {describePlay(play, player, opponents)}
              </li>
            ))
            .reverse()}
        </ol>
      )}
    </div>
  );
}
